import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Keyboard, RotateCcw, X, Loader2, Trophy } from "lucide-react";
import { Button } from "../ui/button";
import { TypingTargetText } from "./TypingTargetText";

const API = "/api";
const RUN_SECONDS = 30;

// 30-second typing sprint for students waiting between live-drill questions.
// Junior types simple words, Blocks types ★1-2 Python solutions, Python types ★3-4 (indents + punctuation count).
export const TypingSprint = ({ mode, sessionId, token, onClose }) => {
  const [phase, setPhase] = useState("loading"); // loading | ready | running | done | error
  const [texts, setTexts] = useState([]);
  const [idx, setIdx] = useState(0);
  const [typed, setTyped] = useState("");
  const [timeLeft, setTimeLeft] = useState(RUN_SECONDS);
  const [score, setScore] = useState(null);
  const typedLog = useRef([]); // completed snippet strings, in the order shown
  const startedAt = useRef(null);
  const inputRef = useRef(null);
  const textsRef = useRef([]);
  const reqRef = useRef(0);
  const target = texts[idx] || "";

  const loadTexts = useCallback(async () => {
    const reqId = ++reqRef.current;
    setPhase("loading");
    try {
      const res = await fetch(`${API}/live/typing-text?session_id=${encodeURIComponent(sessionId)}&token=${encodeURIComponent(token)}&mode=${encodeURIComponent(mode || "python")}&count=8`);
      const data = await res.json();
      if (reqId !== reqRef.current) return; // a newer load superseded this one
      if (!res.ok || !data.texts?.length) throw new Error("No text");
      textsRef.current = data.texts;
      setTexts(data.texts);
      setIdx(0);
      setTyped("");
      typedLog.current = [];
      startedAt.current = null;
      setTimeLeft(RUN_SECONDS);
      setScore(null);
      setPhase("ready");
    } catch {
      if (reqId === reqRef.current) setPhase("error");
    }
  }, [sessionId, token, mode]);

  useEffect(() => { loadTexts(); }, [loadTexts]);
  useEffect(() => { if (phase === "ready" || phase === "running") inputRef.current?.focus(); }, [phase, idx]);

  const finish = useCallback((currentTyped) => {
    // Include the in-progress snippet, then score locally with the same rule the server uses
    // (correct characters / characters typed) so the displayed number matches the authoritative one.
    const submitted = [...typedLog.current, currentTyped];
    const t = textsRef.current;
    let correct = 0, total = 0;
    submitted.forEach((s, k) => {
      const tgt = t[k % t.length] || "";
      total += s.length;
      for (let i = 0; i < Math.min(s.length, tgt.length); i++) if (s[i] === tgt[i]) correct++;
    });
    const wpm = Math.round((correct / 5) * (60 / RUN_SECONDS));
    const accuracy = total ? Math.round((correct / total) * 100) : 0;
    setScore({ wpm, accuracy, chars: correct });
    setPhase("done");
    // Server recomputes the score from these characters (self-reported numbers aren't trusted) and,
    // for logged-in players with 90%+ accuracy, ranks them on the Typing / Junior Typing board.
    fetch(`${API}/live/typing-result`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId, participant_token: token, typed: submitted }),
    }).then(async (r) => {
      if (!r.ok) return;
      const board = await r.json();
      setScore((sc) => ({ ...sc, wpm: board.wpm ?? sc.wpm, accuracy: board.accuracy ?? sc.accuracy, chars: board.correct_chars ?? sc.chars, board }));
    }).catch(() => {});
  }, [sessionId, token]);

  // Countdown - starts on the first keystroke
  const typedRef = useRef(""); typedRef.current = typed;
  useEffect(() => {
    if (phase !== "running") return;
    const tick = setInterval(() => {
      const left = RUN_SECONDS - (Date.now() - startedAt.current) / 1000;
      if (left <= 0) {
        clearInterval(tick);
        setTimeLeft(0);
        finish(typedRef.current);
      } else {
        setTimeLeft(left);
      }
    }, 100);
    return () => clearInterval(tick);
  }, [phase, finish]);

  const applyTyped = (next) => {
    if (phase === "ready") { startedAt.current = Date.now(); setPhase("running"); }
    else if (phase !== "running") return;
    if (next.length >= target.length) {
      typedLog.current.push(next);
      setTyped("");
      setIdx((i) => (i + 1) % textsRef.current.length);
      return;
    }
    setTyped(next);
  };

  const onKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();
      applyTyped(typed + "    ");
    }
  };

  const pct = Math.max(0, Math.min(100, (timeLeft / RUN_SECONDS) * 100));

  return (
    <div className="mt-6 text-left rounded-2xl border border-border bg-card p-4 sm:p-5 space-y-4" data-testid="typing-sprint">
      <div className="flex items-center justify-between gap-3">
        <span className="font-heading font-semibold flex items-center gap-2 text-sm">
          <Keyboard className="w-4 h-4 text-primary" /> Typing Sprint
        </span>
        <div className="flex items-center gap-2">
          {(phase === "ready" || phase === "running") && (
            <span className={`font-mono text-2xl font-bold tabular-nums ${timeLeft <= 5 && phase === "running" ? "text-red-500" : "text-foreground"}`} data-testid="typing-timer">
              {Math.ceil(timeLeft)}
            </span>
          )}
          <button onClick={onClose} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted" title="Close" data-testid="typing-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {(phase === "ready" || phase === "running") && (
        <div className="h-1 rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-primary transition-[width] duration-100 ease-linear" style={{ width: `${pct}%` }} />
        </div>
      )}

      {phase === "loading" && (
        <div className="flex items-center justify-center py-8 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading text…</div>
      )}
      {phase === "error" && (
        <div className="text-center py-6 space-y-3">
          <p className="text-sm text-muted-foreground">Couldn't load any text to type right now.</p>
          <Button variant="outline" size="sm" onClick={loadTexts} data-testid="typing-retry-btn">Try again</Button>
        </div>
      )}

      {(phase === "ready" || phase === "running") && (
        <div className="relative rounded-xl bg-muted/40 p-4" data-testid="typing-arena">
          <TypingTargetText target={target} typed={typed} onClick={() => inputRef.current?.focus()} />
          <textarea
            ref={inputRef}
            value={typed}
            onChange={(e) => applyTyped(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={(e) => e.preventDefault()}
            autoCapitalize="off"
            autoCorrect="off"
            autoComplete="off"
            spellCheck={false}
            aria-label="Type the text shown"
            className="absolute inset-0 w-full h-full opacity-0 resize-none cursor-text"
            data-testid="typing-input"
          />
          {phase === "ready" && (
            <p className="mt-3 text-xs text-muted-foreground text-center" data-testid="typing-hint">
              Start typing to begin the {RUN_SECONDS}-second sprint{mode !== "junior" ? " · Tab = 4 spaces, Enter = new line" : ""}
            </p>
          )}
        </div>
      )}

      {phase === "done" && score && (
        <div className="text-center py-4 space-y-4" data-testid="typing-score">
          <div className="grid grid-cols-2 gap-3 max-w-xs mx-auto">
            <div className="rounded-xl bg-primary/10 py-4">
              <div className="font-heading font-bold text-4xl text-primary tabular-nums" data-testid="typing-wpm">{score.wpm}</div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">WPM</div>
            </div>
            <div className="rounded-xl bg-emerald-500/10 py-4">
              <div className="font-heading font-bold text-4xl text-emerald-600 dark:text-emerald-400 tabular-nums" data-testid="typing-accuracy">{score.accuracy}%</div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">Accuracy</div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{score.chars} correct characters in {RUN_SECONDS} seconds</p>
          {score.board && <BoardNote board={score.board} />}
          <Button onClick={loadTexts} className="gap-2 rounded-xl" data-testid="typing-again-btn">
            <RotateCcw className="w-4 h-4" /> Go again
          </Button>
        </div>
      )}
    </div>
  );
};

const BoardNote = ({ board }) => {
  let text;
  if (board.ranked) {
    text = board.personal_best ? `New personal best - you're #${board.rank} on the Typing leaderboard` : `Your best is ${board.best_wpm} wpm - #${board.rank} on the Typing leaderboard`;
  } else if (board.reason === "accuracy") {
    text = `Hit ${board.min_accuracy}%+ accuracy to count for the Typing leaderboard${board.best_wpm ? ` (your best: ${board.best_wpm} wpm, #${board.rank})` : ""}`;
  } else {
    text = "Log in before joining a live drill to get on the Typing leaderboard";
  }
  return (
    <p className={`text-xs flex items-center justify-center gap-1.5 ${board.personal_best ? "text-primary font-medium" : "text-muted-foreground"}`} data-testid="typing-board-note">
      <Trophy className="w-3.5 h-3.5" /> {text} · <Link to="/rankings" className="underline hover:text-foreground">Rankings</Link>
    </p>
  );
};
